import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Video, Mic, MicOff, VideoOff, Users, Loader2, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MeetingRoom() {
  const { meetingId } = useParams();
  
  const [stage, setStage] = useState('loading'); // loading, lobby, joining, meeting, ended, error
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState(null);
  
  // Lobby form
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  
  // Media preview
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [localStream, setLocalStream] = useState(null);
  
  // Daily.co
  const [dailyFrame, setDailyFrame] = useState(null);

  // Load meeting details
  useEffect(() => {
    const loadMeeting = async () => {
      try {
        const response = await fetch(`${API}/api/meeting-intelligence/meetings/${meetingId}/public-info`);
        
        if (!response.ok) {
          throw new Error('Meeting not found');
        }
        
        const data = await response.json();
        setMeeting(data);
        setStage('lobby');
        
      } catch (err) {
        setError(err.message || 'Failed to load meeting');
        setStage('error');
      }
    };
    
    loadMeeting();
  }, [meetingId]);

  // Initialize media preview in lobby
  useEffect(() => {
    if (stage === 'lobby') {
      initMediaPreview();
    }
    
    return () => {
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stage]);

  // Cleanup Daily frame on unmount
  useEffect(() => {
    return () => {
      if (dailyFrame) {
        dailyFrame.destroy();
      }
    };
  }, [dailyFrame]);

  const initMediaPreview = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      setLocalStream(stream);
      
      const videoEl = document.getElementById('preview-video');
      if (videoEl) {
        videoEl.srcObject = stream;
      }
    } catch (err) {
      console.warn('Could not access media devices:', err);
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoEnabled;
        setVideoEnabled(!videoEnabled);
      }
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioEnabled;
        setAudioEnabled(!audioEnabled);
      }
    }
  };

  const joinMeeting = async () => {
    if (!guestName.trim()) {
      setError('Please enter your name');
      return;
    }
    
    setStage('joining');
    setError(null);
    
    try {
      // Register participant and get Daily.co room info
      const registerResponse = await fetch(
        `${API}/api/meeting-intelligence/meetings/${meetingId}/join`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: guestName,
            email: guestEmail || undefined
          })
        }
      );
      
      if (!registerResponse.ok) {
        const errData = await registerResponse.json().catch(() => ({}));
        throw new Error(errData.detail || errData.error || 'Failed to join meeting');
      }
      
      const data = await registerResponse.json();
      console.log('Join response:', data);
      
      // Stop preview stream
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
        setLocalStream(null);
      }
      
      // Load Daily.co
      await loadDailyFrame(data.room_url, data.token);
      setStage('meeting');
      
    } catch (err) {
      console.error('Join error:', err);
      setError(err.message || 'Failed to join meeting');
      setStage('lobby');
    }
  };

  const loadDailyFrame = async (roomUrl, meetingToken) => {
    console.log('Loading Daily.co frame with URL:', roomUrl);
    
    // Load Daily.co script if not already loaded
    if (!window.DailyIframe) {
      console.log('Loading Daily.co script...');
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/@daily-co/daily-js';
        script.onload = () => {
          console.log('Daily.co script loaded');
          resolve();
        };
        script.onerror = (e) => {
          console.error('Failed to load Daily.co script:', e);
          reject(new Error('Failed to load video library'));
        };
        document.head.appendChild(script);
      });
    }
    
    // Wait for script to initialize
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const container = document.getElementById('daily-container');
    if (!container) {
      throw new Error('Video container not found');
    }
    
    console.log('Creating Daily frame...');
    
    try {
      const frame = window.DailyIframe.createFrame(container, {
        iframeStyle: {
          width: '100%',
          height: '100%',
          border: '0',
          borderRadius: '0'
        },
        showLeaveButton: true,
        showFullscreenButton: true
      });
      
      // Handle events
      frame.on('joining-meeting', () => {
        console.log('Joining meeting...');
      });
      
      frame.on('joined-meeting', () => {
        console.log('Joined meeting successfully');
      });
      
      frame.on('left-meeting', () => {
        console.log('Left meeting');
        setStage('ended');
      });
      
      frame.on('error', (e) => {
        console.error('Daily.co error:', e);
      });
      
      console.log('Joining room:', roomUrl);
      
      // Join the meeting
      await frame.join({
        url: roomUrl,
        token: meetingToken || undefined,
        userName: guestName,
        startVideoOff: !videoEnabled,
        startAudioOff: !audioEnabled
      });
      
      setDailyFrame(frame);
      console.log('Daily frame created and joined');
      
    } catch (err) {
      console.error('Daily.co error:', err);
      throw new Error('Failed to connect to video room: ' + (err.message || err));
    }
  };

  // Render based on stage
  if (stage === 'loading') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-slate-400">Loading meeting...</p>
        </div>
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Unable to Join</h2>
            <p className="text-slate-400 mb-4">{error || 'Meeting not found or has ended.'}</p>
            <Button onClick={() => window.location.reload()} className="bg-orange-500 hover:bg-orange-600">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (stage === 'ended') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardContent className="pt-6 text-center">
            <Video className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Meeting Ended</h2>
            <p className="text-slate-400 mb-4">
              Thank you for joining! The recording is being processed. You will receive the meeting summary via email shortly.
            </p>
            <Button onClick={() => window.close()} className="bg-orange-500 hover:bg-orange-600">
              Close Window
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (stage === 'meeting' || stage === 'joining') {
    return (
      <div className="h-screen bg-slate-900 flex flex-col">
        <div id="daily-container" className="flex-1 w-full" style={{ minHeight: '100vh' }} />
      </div>
    );
  }

  // Lobby stage
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full bg-slate-800 border-slate-700">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-white">
            {meeting?.title || 'Join Meeting'}
          </CardTitle>
          {meeting?.project_name && (
            <CardDescription className="text-slate-400">
              Project: {meeting.project_name}
            </CardDescription>
          )}
          <div className="flex items-center justify-center gap-2 mt-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
              <span className="w-2 h-2 rounded-full bg-green-500 mr-1 animate-pulse"></span>
              Recording enabled
            </span>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Video Preview */}
            <div className="space-y-4">
              <div className="relative aspect-video bg-slate-700 rounded-lg overflow-hidden">
                {videoEnabled ? (
                  <video
                    id="preview-video"
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <VideoOff className="h-12 w-12 text-slate-500 mx-auto mb-2" />
                      <p className="text-slate-500 text-sm">Camera off</p>
                    </div>
                  </div>
                )}
                
                {/* Media controls */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  <Button
                    size="icon"
                    variant={audioEnabled ? "default" : "destructive"}
                    onClick={toggleAudio}
                    className="rounded-full"
                  >
                    {audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                  </Button>
                  <Button
                    size="icon"
                    variant={videoEnabled ? "default" : "destructive"}
                    onClick={toggleVideo}
                    className="rounded-full"
                  >
                    {videoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              {meeting?.participant_count > 0 && (
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Users className="h-4 w-4" />
                  <span>{meeting.participant_count} participant(s) in meeting</span>
                </div>
              )}
            </div>
            
            {/* Join Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">Your Name *</Label>
                <Input
                  id="name"
                  placeholder="Enter your name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && joinMeeting()}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                  data-testid="guest-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email (for meeting summary)</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && joinMeeting()}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                  data-testid="guest-email-input"
                />
                <p className="text-xs text-slate-500">
                  Optional - receive meeting notes after the call
                </p>
              </div>
              
              {error && (
                <div className="text-red-400 text-sm bg-red-500/10 p-2 rounded border border-red-500/20">
                  {error}
                </div>
              )}
              
              <Button
                className="w-full bg-orange-500 hover:bg-orange-600"
                size="lg"
                onClick={joinMeeting}
                disabled={stage === 'joining' || !guestName.trim()}
                data-testid="join-meeting-btn"
              >
                {stage === 'joining' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Joining...
                  </>
                ) : (
                  <>
                    <Video className="h-4 w-4 mr-2" />
                    Join Meeting
                  </>
                )}
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                This meeting will be recorded automatically for AI-powered notes and transcription.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
